import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { TiveSessionService } from '../auth/tive-session.service';

/**
 * Route guard that checks whether the current user has the ability
 * specified in `route.data['requiredAbility']`.
 *
 * Waits for the session to be fully initialized (loginByEmail complete)
 * before evaluating, so that deep links work correctly on a fresh page load.
 *
 * If no `requiredAbility` is set on the route, access is always allowed.
 * If the user lacks the ability, they are redirected to '/'.
 */
export const abilityGuard: CanActivateFn = (route) => {
  const requiredAbility = route.data?.['requiredAbility'] as string | undefined;

  // No ability requirement → always allow
  if (!requiredAbility) return true;

  const session = inject(TiveSessionService);
  const router = inject(Router);

  return toObservable(session.initialized).pipe(
    filter((initialized) => initialized),
    take(1),
    map(() => {
      const abilities = session.loginUser()?.abilities ?? {};
      return abilities[requiredAbility] ? true : router.createUrlTree(['/']);
    }),
  );
};
